/**
 * Course Repository Interface
 * Defines contract for course data access
 */

export class ICourseRepository {
  /**
   * Get course by ID
   * @param {string} courseId
   * @returns {Promise<Course|null>}
   */
  async getCourseById(courseId) {
    throw new Error('Method not implemented');
  }

  /**
   * Get all courses
   * @returns {Promise<Course[]>}
   */
  async getAllCourses() {
    throw new Error('Method not implemented');
  }

  /**
   * Get section by ID
   * @param {string} courseId
   * @param {string} sectionId
   * @returns {Promise<Section|null>}
   */
  async getSectionById(courseId, sectionId) {
    throw new Error('Method not implemented');
  }

  /**
   * Get all sections for a course
   * @param {string} courseId
   * @returns {Promise<Section[]>}
   */
  async getSectionsForCourse(courseId) {
    throw new Error('Method not implemented');
  }

  /**
   * Get question by ID
   * @param {string} courseId
   * @param {string} questionId
   * @returns {Promise<Question|null>}
   */
  async getQuestionById(courseId, questionId) {
    throw new Error('Method not implemented');
  }

  /**
   * Get all questions for a section
   * @param {string} courseId
   * @param {string} sectionId
   * @returns {Promise<Question[]>}
   */
  async getQuestionsForSection(courseId, sectionId) {
    throw new Error('Method not implemented');
  }

  /**
   * Get all questions grouped by section for a course
   * @param {string} courseId
   * @returns {Promise<Record<string, Question[]>>}
   */
  async getQuestionsForCourse(courseId) {
    throw new Error('Method not implemented');
  }

  /**
   * Create a new course
   * @param {Object} courseData
   * @returns {Promise<Course>}
   */
  async createCourse(courseData) {
    throw new Error('Method not implemented');
  }

  /**
   * Update an existing course
   * @param {string} courseId
   * @param {Object} courseData
   * @returns {Promise<Course>}
   */
  async updateCourse(courseId, courseData) {
    throw new Error('Method not implemented');
  }

  /**
   * Delete a course
   * @param {string} courseId
   * @returns {Promise<boolean>}
   */
  async deleteCourse(courseId) {
    throw new Error('Method not implemented');
  }

  /**
   * Get all email templates for a course (defaults + overrides)
   * @param {string} courseId
   * @returns {Promise<{courseId: string, templates: Object[], templateTypes: string[], supportedVariables: Object}>}
   */
  async getCourseEmailTemplates(courseId) {
    throw new Error('Method not implemented');
  }

  /**
   * Create or update a single course email template override
   * @param {string} courseId
   * @param {string} templateType
   * @param {{subject?: string, body?: string, isEnabled?: boolean}} templateData
   * @returns {Promise<Object>}
   */
  async upsertCourseEmailTemplate(courseId, templateType, templateData) {
    throw new Error('Method not implemented');
  }

  /**
   * Remove override and restore default template for a type
   * @param {string} courseId
   * @param {string} templateType
   * @returns {Promise<Object>}
   */
  async resetCourseEmailTemplate(courseId, templateType) {
    throw new Error('Method not implemented');
  }

  /**
   * Trigger course study reminder emails for inactive learners
   * @param {string} courseId
   * @param {{idleDays?: number, maxRecipients?: number}} options
   * @returns {Promise<Object>}
   */
  async sendCourseStudyReminders(courseId, options) {
    throw new Error('Method not implemented');
  }

  /**
   * Retrieve email dispatch logs with optional filters and pagination.
   * @param {{courseId?: string, status?: string, templateType?: string, provider?: string, search?: string, page?: number, pageSize?: number}} filters
   * @returns {Promise<{logs: Object[], pagination: Object}>}
   */
  async getEmailDispatchLogs(filters) {
    throw new Error('Method not implemented');
  }

  /**
   * Retrieve effective global email delivery configuration.
   * @returns {Promise<{config: Object, defaults: Object, deliveryModes: string[], hasPersistedOverride: boolean}>}
   */
  async getEmailDeliveryConfiguration() {
    throw new Error('Method not implemented');
  }

  /**
   * Create or update persisted global email delivery configuration.
   * @param {Object} configData
   * @returns {Promise<{config: Object, defaults: Object, deliveryModes: string[], hasPersistedOverride: boolean}>}
   */
  async upsertEmailDeliveryConfiguration(configData) {
    throw new Error('Method not implemented');
  }

  /**
   * Remove persisted global email delivery configuration override.
   * @returns {Promise<{config: Object, defaults: Object, deliveryModes: string[], hasPersistedOverride: boolean}>}
   */
  async resetEmailDeliveryConfiguration() {
    throw new Error('Method not implemented');
  }
}
